import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect} from 'react';

import {getMethodNameOrPlaceholder} from 'procedures/design/procedureMethodNodeCommonConsts';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {buildProcedureValidationErrorsForPanel} from 'procedures/design/validation/procedureValidationUtils';
import {trackViewedProcedureValidationErrorsPanel} from 'procedures/procedureAnalyticsEventsUtil';
import {MethodNode} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {GRAY, RED} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface ProcedureErrorToolTipProps {
  methodNodes: Array<MethodNode>;
}

interface ProcedureErrorPanelProps {
  procedureValidationErrors: Array<ProcedureValidationError>;
  methodNodes: Array<MethodNode>;
}

function DisplayValueForErrorCount(props: {count: number}): JSX.Element | null {
  return props.count > 1 ? <div className={css(styles.errorCount)}>{` x${props.count}`}</div> : null;
}

function ProcedureErrorPanel(props: ProcedureErrorPanelProps): JSX.Element {
  const errorsForPanel = buildProcedureValidationErrorsForPanel(props.procedureValidationErrors);
  const methodCidToMethodNameMap = objectFromEntries(props.methodNodes.map((node) => [node.cid, node.name]));
  const {procedureMetadata} = ProcedurePageContext.useContext();
  const procedureLabel = getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    procedureMetadata.procedureType,
    {
      stringCase: StringCase.LOWER,
    }
  );

  useEffect(() => {
    trackViewedProcedureValidationErrorsPanel(procedureMetadata.id, procedureMetadata.procedureType);
  }, [procedureMetadata.id, procedureMetadata.procedureType]);

  return (
    <div className={css(styles.errorListWrapper)}>
      <div>
        <div className={css(styles.errorPanelHeading)}>{`Resolve errors to use ${procedureLabel}`}</div>
        {`Resolve all errors to use your ${procedureLabel} in a study and plan experiments.`}
      </div>
      {Object.keys(errorsForPanel.flowchartViewErrors).map((errorMsg, index) => (
        <div className={classNames(css(styles.error))} key={index}>
          <BenchlingIcon className={css(styles.validationErrorIcon)} icon="exclamation-circle" />
          {errorMsg}
          <DisplayValueForErrorCount count={errorsForPanel.flowchartViewErrors[errorMsg]} />
        </div>
      ))}
      {Object.keys(errorsForPanel.methodViewErrorsByMethodId).map((methodCid) => (
        <div key={methodCid}>
          <div className={classNames(css(styles.errorPanelHeading))}>
            {getMethodNameOrPlaceholder(methodCidToMethodNameMap[methodCid], procedureMetadata.procedureType)}
          </div>
          {Object.keys(errorsForPanel.methodViewErrorsByMethodId[methodCid]).map((errorMessage, index) => (
            <div className={css(styles.error)} key={index}>
              <BenchlingIcon className={css(styles.validationErrorIcon)} icon="exclamation-circle" />
              {errorMessage}
              <DisplayValueForErrorCount
                count={errorsForPanel.methodViewErrorsByMethodId[methodCid][errorMessage]}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
export default function ProcedureErrorToolTip(props: ProcedureErrorToolTipProps): JSX.Element {
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();

  return procedureValidationErrors.length > 0 ? (
    <Popover
      placement={FloatingPlacement.BOTTOM}
      renderContent={() => (
        <ProcedureErrorPanel
          procedureValidationErrors={procedureValidationErrors}
          methodNodes={props.methodNodes}
        />
      )}
    >
      <div className={css(styles.errorStatusBadge)}>
        {procedureValidationErrors.length}
        <BenchlingIcon className={css(styles.validationErrorIcon)} icon="exclamation-circle" />
      </div>
    </Popover>
  ) : (
    <></>
  );
}

const styles = StyleSheet.create({
  errorListWrapper: {
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  errorCount: {
    fontWeight: 600,
  },
  error: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginBottom: '4px',
  },
  errorPanelHeading: {
    fontWeight: 600,
    fontSize: '13px',
  },
  errorStatusBadge: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    margin: '4px',
    gap: '8px',
    height: '28px',
    background: GRAY,
    borderRadius: '4px',
  },
  validationErrorIcon: {
    color: RED,
    width: '15px',
    height: '15px',
  },
});
